import {
    Vector3,
    Scene,
    Group,
    Mesh,
    WebGLRenderer,
    OrthographicCamera,
    Camera,
    AmbientLight,
    CylinderBufferGeometry,
    MeshBasicMaterial,
    CylinderGeometry,
    Vector4
} from "three";

type TConstructor<T = {}> = new (...constructorArguments: any[]) => T;

export class AxisUI {
    private _cameraToFollow: Camera;
    private _renderer: WebGLRenderer;
    private _scene: Scene;

    private _camera: OrthographicCamera;
    private _forward: Vector3;
    private _up: Vector3;
    private _side: Vector3;

    private _axisGroup: Group;
    private _axes: Mesh[];
    private _axisLabels: Group[];

    private _visible: boolean;

    private _coreViewport: Vector4;
    private _axisViewport: Vector4;


    public set cameraToFollow(cameraToFollow: Camera) {
        this._cameraToFollow = cameraToFollow;
    }

    public constructor(cameraToFollow: Camera, renderer: WebGLRenderer) {
        this._cameraToFollow = cameraToFollow;
        this._forward = new Vector3(0, 1, 0);
        this._up = new Vector3(0, 0, 1);
        this._side = new Vector3().crossVectors(this._up, this._forward);

        this._camera = new OrthographicCamera(-50, 50, 50, -50, 1, 1000);
        this._camera.position.set(this._forward.x * 200, this._forward.y * 200, this._forward.z * 200);
        this._camera.up.copy(this._up);
        this._camera.lookAt(0, 0, 0);
        this._camera.updateMatrixWorld(true);

        this._renderer = renderer;
        this._scene = new Scene();
        this._coreViewport = new Vector4();
        this._axisViewport = new Vector4();
        this.resize();

        this._axisGroup = new Group();
        this._scene.add(this._axisGroup);

        this._axes = this.makeArrayOfObjects(6, Mesh);
        this.setupAxes(this._axes);
        this._axisGroup.add(...this._axes);

        this._axisLabels = this.makeArrayOfObjects(3, Group);
        this.setupAxisLabels(this._axisLabels);
        this._axisGroup.add(...this._axisLabels);

        const ambientLight = new AmbientLight(0xFFFFFF);
        this._scene.add(ambientLight);

        this._scene.add(this._camera);

        this._visible = true;

    }

    public render(): void {

        if (!this._visible)
            return;

        this._renderer.setViewport(this._axisViewport);

        // update camera orientation
        this._cameraToFollow.matrixWorld.extractBasis(this._side, this._up, this._forward);

        this._camera.position.set(this._forward.x * 200, this._forward.y * 200, this._forward.z * 200);
        this._camera.up.copy(this._up);

        // Yokouchi rotation test
        //this._camera.lookAt(25, 25, 25);
        this._camera.lookAt(0, 0, 0);

        this._camera.updateMatrixWorld(true);

        this._axisLabels[0].up.copy(this._up);
        this._axisLabels[0].lookAt(this._camera.position);
        this._axisLabels[1].up.copy(this._up);
        this._axisLabels[1].lookAt(this._camera.position);
        this._axisLabels[2].up.copy(this._up);
        this._axisLabels[2].lookAt(this._camera.position);

        this._renderer.render(this._scene, this._camera);

        this._renderer.setViewport(this._coreViewport);
    }

    private setupAxes(axes: Mesh[]): void {
        const xAxis = axes[0];
        const xAxisGeom = new CylinderBufferGeometry(2, 2, 30, 12);
        xAxis.geometry = xAxisGeom;
        xAxis.material = new MeshBasicMaterial({ color: 0xFF00FF, depthTest: false });
        xAxis.frustumCulled = false;
        xAxis.up.set(1, 0, 0);
        xAxis.lookAt(0, -1, 0);
        xAxis.position.set(15, 0, 0);

        const yAxis = axes[1];
        const yAxisGeom = new CylinderBufferGeometry(2, 2, 30, 12);
        yAxis.geometry = yAxisGeom;
        yAxis.material = new MeshBasicMaterial({ color: 0x00FF00, depthTest: false  });
        yAxis.frustumCulled = false;
        yAxis.position.set(0, 15, 0);

        const zAxis = axes[2];
        const zAxisGeom = new CylinderBufferGeometry(2, 2, 30, 12);
        zAxis.geometry = zAxisGeom;
        zAxis.material = new MeshBasicMaterial({ color: 0x00FFFF, depthTest: false  });
        zAxis.frustumCulled = false;
        zAxis.up.set(0, 0, 1);
        zAxis.lookAt(0, -1, 0);
        zAxis.position.set(0, 0, 15);

        const xArrow = axes[3];
        const xArrowGeom = new CylinderBufferGeometry(0, 5, 10, 12);
        xArrow.geometry = xArrowGeom;
        xArrow.material = new MeshBasicMaterial({ color: 0xFF00FF, depthTest: false  });
        xArrow.frustumCulled = false;
        xArrow.up.set(1, 0, 0);
        xArrow.lookAt(0, -1, 0);
        xArrow.position.set(35, 0, 0);

        const yArrow = axes[4];
        const yArrowGeom = new CylinderBufferGeometry(0, 5, 10, 12);
        yArrow.geometry = yArrowGeom;
        yArrow.material = new MeshBasicMaterial({ color: 0x00FF00, depthTest: false  });
        yArrow.frustumCulled = false;
        yArrow.position.set(0, 35, 0);

        const zArrow = axes[5];
        const zArrowGeom = new CylinderBufferGeometry(0, 5, 10, 12);
        zArrow.geometry = zArrowGeom;
        zArrow.material = new MeshBasicMaterial({ color: 0x00FFFF, depthTest: false  });
        zArrow.frustumCulled = false;
        zArrow.up.set(0, 0, 1);
        zArrow.lookAt(0, -1, 0);
        zArrow.position.set(0, 0, 35);
    }

    private setupAxisLabels(axes: Group[]): void {
        const x = axes[0];

        const x_left_geo = new CylinderGeometry(1, 1, 12, 12);
        const x_left_mat = new MeshBasicMaterial({ color: 0xFF00FF, depthTest: false  });
        const x_left = new Mesh(x_left_geo, x_left_mat);
        x_left.rotation.z = -Math.PI / 5;
        x_left.frustumCulled = false;
        x.add(x_left)

        const x_right_geo = new CylinderGeometry(1, 1, 12, 12);
        const x_right_mat = new MeshBasicMaterial({ color: 0xFF00FF, depthTest: false  });
        const x_right = new Mesh(x_right_geo, x_right_mat);
        x_right.rotation.z = Math.PI / 5;
        x_right.frustumCulled = false;
        x.add(x_right)

        x.frustumCulled = false;
        x.position.set(45, 0, 0);

        const y = axes[1];

        const y_left_geo = new CylinderGeometry(1, 1, 5, 12);
        const y_left_mat = new MeshBasicMaterial({ color: 0x00FF00, depthTest: false  });
        const y_left = new Mesh(y_left_geo, y_left_mat);
        y.add(y_left)
        y_left.position.set(2, 2, 0);
        y_left.rotation.z = -Math.PI / 5;
        y_left.frustumCulled = false;

        const y_right_geo = new CylinderGeometry(1, 1, 5, 12);
        const y_right_mat = new MeshBasicMaterial({ color: 0x00FF00, depthTest: false  });
        const y_right = new Mesh(y_right_geo, y_right_mat);
        y.add(y_right)
        y_right.position.set(-2, 2, 0);
        y_right.rotation.z = Math.PI / 5;
        y_right.frustumCulled = false;


        const y_bottom_geo = new CylinderGeometry(1, 1, 6, 12);
        const y_bottom_mat = new MeshBasicMaterial({ color: 0x00FF00, depthTest: false  });
        const y_bottom = new Mesh(y_bottom_geo, y_bottom_mat);
        y.add(y_bottom)
        y_bottom.position.set(0, -3, 0);
        y_bottom.frustumCulled = false;

        y.frustumCulled = false;
        y.position.set(0, 45, 0);

        const z = axes[2];

        const z_top_geo = new CylinderGeometry(1, 1, 7, 12);
        const z_top_mat = new MeshBasicMaterial({ color: 0x00FFFF, depthTest: false  });
        const z_top = new Mesh(z_top_geo, z_top_mat);
        z.add(z_top)
        z_top.position.set(0, 4, 0);
        z_top.rotation.z = -Math.PI / 2;
        z_top.frustumCulled = false;

        const z_middle_geo = new CylinderGeometry(1, 1, 10, 12);
        const z_middle_mat = new MeshBasicMaterial({ color: 0x00FFFF, depthTest: false  });
        const z_middle = new Mesh(z_middle_geo, z_middle_mat);
        z.add(z_middle)
        // z_middle.position.set(-3, 3, 0);
        z_middle.rotation.z = -Math.PI / 5;
        z_middle.frustumCulled = false;

        const z_bottom_geo = new CylinderGeometry(1, 1, 7, 12);
        const z_bottom_mat = new MeshBasicMaterial({ color: 0x00FFFF, depthTest: false  });
        const z_bottom = new Mesh(z_bottom_geo, z_bottom_mat);
        z.add(z_bottom)
        z_bottom.position.set(0, -4, 0);
        z_bottom.rotation.z = Math.PI / 2;
        z_bottom.frustumCulled = false;

        z.frustumCulled = false;
        z.position.set(0, 0, 46);
    }

    private makeArrayOfObjects = <T>(length: number, type: TConstructor<T>, ...constructionArguments: any[]): T[] => {
        return Array.from({ length: length }, () => {
            return new type(...constructionArguments);
        });
    };

    public setVisibile(visible: boolean) {
        this._visible = visible;
    }

    public resize() {
        this._renderer.getViewport(this._coreViewport);
        this._axisViewport.set(this._coreViewport.z - 110, this._coreViewport.w - 110, 100, 100);
    }

    public setCamera(camera: Camera) {
        this._cameraToFollow = camera;
    }

    public flipXY(yUp :boolean) {
        const xAxis = this._axes[0];
        const yAxis = this._axes[1];
        const x = this._axisLabels[0];
        const y = this._axisLabels[1];
        const xArrow = this._axes[3];
        const yArrow = this._axes[4];
        if (yUp) {
            x.position.set(0, 45, 0);
            y.position.set(45, 0, 0);
            xAxis.material = new MeshBasicMaterial({ color: 0x00FF00, depthTest: false  });
            xArrow.material = new MeshBasicMaterial({ color: 0x00FF00, depthTest: false  });
            yAxis.material = new MeshBasicMaterial({ color: 0xFF00FF, depthTest: false  });
            yArrow.material = new MeshBasicMaterial({ color: 0xFF00FF, depthTest: false  });
        } else {
            x.position.set(45, 0, 0);
            y.position.set(0, 45, 0);
            xAxis.material = new MeshBasicMaterial({ color: 0xFF00FF, depthTest: false  });
            xArrow.material = new MeshBasicMaterial({ color: 0xFF00FF, depthTest: false  });
            yAxis.material = new MeshBasicMaterial({ color: 0x00FF00, depthTest: false  });
            yArrow.material = new MeshBasicMaterial({ color: 0x00FF00, depthTest: false  });
        }
    }
}